import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  Button,
  Card,
  CardContent,
  CardMedia,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Building2,
  Users,
  Home,
  Award,
  CheckCircle2,
  MapPin,
  Phone,
  Mail,
  Clock,
} from 'lucide-react';

const AgencyPage = () => {
  const stats = [
    { icon: <Home />, value: '10,000+', label: 'Properties Sold' },
    { icon: <Users />, value: '50+', label: 'Expert Agents' },
    { icon: <Award />, value: '15+', label: 'Years Experience' },
    { icon: <Building2 />, value: '20+', label: 'Office Locations' },
  ];

  const values = [
    {
      title: 'Excellence',
      description: 'We strive for excellence in every transaction and client interaction.',
    },
    {
      title: 'Integrity',
      description: 'We conduct our business with the highest level of integrity and transparency.',
    },
    {
      title: 'Innovation',
      description: 'We leverage cutting-edge technology to provide the best service possible.',
    },
    {
      title: 'Client Focus',
      description: 'Our clients needs and satisfaction are at the heart of everything we do.',
    },
  ];

  const offices = [
    {
      city: 'New York',
      image: 'https://images.pexels.com/photos/2224861/pexels-photo-2224861.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      address: '123 Madison Ave, New York, NY 10016',
      phone: '+1 (555) 123-4567',
    },
    {
      city: 'Los Angeles',
      image: 'https://images.pexels.com/photos/1134176/pexels-photo-1134176.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      address: '456 Wilshire Blvd, Los Angeles, CA 90036',
      phone: '+1 (555) 234-5678',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom fontWeight={700}>
            DwellHub Real Estate
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
            Your Trusted Partner in Real Estate Since 2008
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
          >
            Contact Us
          </Button>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  height: '100%',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>
                  {stat.icon}
                </Box>
                <Typography variant="h4" component="div" fontWeight={700}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* About Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom fontWeight={700}>
                About DwellHub
              </Typography>
              <Typography paragraph>
                DwellHub is a leading real estate agency committed to helping our clients
                achieve their real estate goals through expert guidance, innovative
                technology, and personalized service.
              </Typography>
              <List>
                {[
                  'Expert team of licensed real estate professionals',
                  'Comprehensive market knowledge',
                  'Cutting-edge technology and tools',
                  'Personalized service for every client',
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle2 size={20} color={theme.palette.primary.main} />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                alt="Team meeting"
                sx={{
                  width: '100%',
                  height: 400,
                  objectFit: 'cover',
                  borderRadius: 2,
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom fontWeight={700}>
          Our Values
        </Typography>
        <Typography textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          The principles that guide everything we do
        </Typography>
        <Grid container spacing={4}>
          {values.map((value, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Office Locations */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom fontWeight={700}>
            Our Offices
          </Typography>
          <Typography textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Visit us at one of our convenient locations
          </Typography>
          <Grid container spacing={4}>
            {offices.map((office, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={office.image}
                    alt={office.city}
                  />
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {office.city} Office
                    </Typography>
                    <Stack spacing={2}>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <MapPin size={16} style={{ marginRight: 8 }} />
                        {office.address}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone size={16} style={{ marginRight: 8 }} />
                        {office.phone}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Clock size={16} style={{ marginRight: 8 }} />
                        Mon-Fri: 9:00 AM - 6:00 PM
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default AgencyPage;